import {Box, Grid, Stack, Typography} from '@mui/material';
import {AppButton, AppIconButton} from 'components/buttons';
import {AppCard} from 'components/card';
import {AppConfirmationDialog, AppConfirmationDialogRefType} from 'components/dialogs';
import {AppControlledEditor} from 'components/form/AppTextEditor';
import {Avatar} from 'components/Theme';
import {format} from 'date-fns';
import {hasTextContent, safeParseJSON} from 'global';
import {useCreateLogApi, useRemoveActivitiesByIds, useUpdateActivityApi} from 'hooks';
import parser from 'html-react-parser';
import {Edit, Trash} from 'iconsax-react';
import {ActivityModel, CommentStatusMapper, CommentUserInfo} from 'models';
import {LOGS_CONFIG_ENUM, LOGS_EVENT_TYPE, LogsResponseModel, ModifiedItemStatusForItemDataLogModel} from 'models/logs';
import React, {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';

interface BoardItemCommentProps {
    comment: ActivityModel;
    currentCommentStatus: string;
    boardId?: string;
    removeActivity: (activityId: string) => void;
    editCommentHandler?: (comment: ActivityModel) => void;
}
export const BoardItemComment: React.FC<BoardItemCommentProps> = ({
    comment,
    removeActivity,
    currentCommentStatus,
    boardId,
    editCommentHandler,
}) => {
    const rawStatus = typeof comment.status === 'string' ? comment.status : '';
    const status: CommentStatusMapper = safeParseJSON(
        rawStatus ||
            JSON.stringify({
                Active: true,
                Deleted: {status: false, timestamp: undefined},
                Updated: [],
            }),
        {
            Active: true,
            Deleted: {status: false, timestamp: undefined},
            Updated: [],
        },
    );
    const commentConfirmationDialiogRef = useRef<AppConfirmationDialogRefType>(null);
    const [isEditing, setIsEditing] = useState(false);
    const {userId, firstName, lastName} = useSelector(sessionSelector);
    const user: CommentUserInfo = React.useMemo(() => {
        try {
            return JSON.parse(comment.user);
        } catch {
            return {firstName: '', lastName: '', email: '', userId: ''} as CommentUserInfo;
        }
    }, [comment.user]);
    const {removeActivitiesByIdsLoading, removeActivitiesByIdsResponse} = useRemoveActivitiesByIds();
    const {updateActivity, updateActivityLoading, updateActivityResponse} = useUpdateActivityApi();
    const {createLog} = useCreateLogApi();
    const canUserEditDeleteComment: boolean = comment.userId === userId && currentCommentStatus === 'Active';

    const formattedDate = () => {
        if (status.Active === false && status.Deleted && status.Deleted.timestamp) {
            return format(new Date(status.Deleted.timestamp), ' MM/dd/yyyy | hh:mm a');
        } else if (status.Active === false && status.Updated && status.Updated.length > 0) {
            return format(new Date(status.Updated[0].timestamp), ' MM/dd/yyyy | hh:mm a');
        } else {
            return format(new Date(comment.activityDate), ' MM/dd/yyyy | hh:mm a');
        }
    };
    const handleCommentDelete = () => {
        status.Active = false;
        status.Deleted = {status: true, timestamp: new Date().toISOString()};
        updateActivity({
            variables: {
                input: {
                    ...comment,
                    data: comment.data,
                    status: JSON.stringify(status),
                    description: comment.data,
                    objectId: comment.objectId,
                    objectType: comment.objectType,
                },
            },
        });
        removeActivity(comment.id);
        // removeActivitiesByIds({
        //     variables: {
        //         ids: [comment.id],
        //     },
        // });
    };
    const {control, handleSubmit, setValue} = useForm<{comment: string}>({
        defaultValues: {comment: comment.data as string},
    });

    const handleCommentEditClose = () => {
        setValue('comment', comment.data as string);
        setIsEditing(false);
    };
    // 1. Pull out the raw HTML:
    const rawHtml = typeof comment.data === 'string' ? comment.data : '';
    // 2. Strip out contentEditable attributes:
    const withoutContentEditable = rawHtml.replace(/contenteditable=".*?"/gi, '');
    // 3. Parse the sanitized HTML:
    const sanitizedHtml = withoutContentEditable.replace(/^<div[^>]*class="ql-editor"[^>]*>([\s\S]*)<\/div>$/, '$1');
    //
    const onSubmit = (data: {comment: string}) => {
        if (comment.objectId && comment.objectType) {
            if (!status.Updated) status.Updated = [];
            status.Deleted = {status: false};
            status.Updated.unshift({value: comment.data, timestamp: new Date().toISOString()});
            updateActivity({
                variables: {
                    input: {
                        ...comment,
                        data: data.comment,
                        status: JSON.stringify(status),
                        description: data.comment,
                        objectId: comment.objectId,
                        objectType: comment.objectType,
                    },
                },
            });
        }
    };

    useEffect(() => {
        if (!updateActivityLoading && updateActivityResponse) {
            if (comment.objectId && comment.objectType) {
                const logValue: LogsResponseModel<ModifiedItemStatusForItemDataLogModel> = {
                    ModifiedBy: `${firstName} ${lastName}`,
                    Modification: {
                        PreviousStatusName: comment.data,
                        NewStatusName: updateActivityResponse.data,
                    },
                };
                createLog(
                    LOGS_CONFIG_ENUM.BOARDITEMDATA_UPDATE_STATUSCHANGE,
                    JSON.stringify(logValue),
                    LOGS_EVENT_TYPE.UPDATECOMMENT,
                    comment.objectId,
                    'ItemData',
                );
            }
            if (editCommentHandler) {
                editCommentHandler({...comment, data: updateActivityResponse.data});
            }
            setIsEditing(false);
        }
    }, [updateActivityLoading, updateActivityResponse]);

    useEffect(() => {
        if (!removeActivitiesByIdsLoading && removeActivitiesByIdsResponse) {
            if (comment.objectId && comment.objectType) {
                const logValue: LogsResponseModel<ModifiedItemStatusForItemDataLogModel> = {
                    ModifiedBy: `${firstName} ${lastName}`,
                    Modification: {
                        PreviousStatusName: comment.data,
                    },
                };
                createLog(
                    LOGS_CONFIG_ENUM.BOARDITEMDATA_UPDATE_STATUSCHANGE,
                    JSON.stringify(logValue),
                    LOGS_EVENT_TYPE.REMOVECOMMENT,
                    comment.objectId,
                    'ItemData',
                );
            }
            removeActivity(comment.id);
            commentConfirmationDialiogRef.current?.handleClose();
        }
    }, [removeActivitiesByIdsLoading, removeActivitiesByIdsResponse]);

    if (currentCommentStatus === 'Updated' && status.Updated && status.Updated.length === 0) {
        return null;
    }

    return (
        <Grid item xs={12}>
            <AppCard paddingBottom="0px" style={{marginRight: 7}}>
                <Grid container wrap="nowrap" alignItems="center" spacing={1}>
                    <Grid item>
                        <Avatar
                            sx={{width: 24, height: 24}}
                            size="sm"
                            alt={`${user?.firstName || ''} ${user?.lastName || ''}`}
                        />
                    </Grid>
                    <Grid item xs zeroMinWidth>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            {user && (
                                <Typography variant="subtitle1">
                                    {user.firstName} {user.lastName}
                                </Typography>
                            )}
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="caption" color="secondary">
                                    {formattedDate()}
                                </Typography>
                                {canUserEditDeleteComment && (
                                    <>
                                        <AppIconButton color="primary" size="small" onClick={() => setIsEditing(true)}>
                                            <Edit variant="Outline" />
                                        </AppIconButton>
                                        <AppIconButton
                                            color="error"
                                            size="small"
                                            onClick={() => commentConfirmationDialiogRef.current?.handleOpen()}
                                        >
                                            <Trash variant="Bold" />
                                        </AppIconButton>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>

                {canUserEditDeleteComment && isEditing ? (
                    <form noValidate>
                        <Grid container spacing={1} p={1}>
                            <Grid item xs={12}>
                                <AppControlledEditor
                                    control={control}
                                    label={''}
                                    scopeId={boardId}
                                    rules={{
                                        validate: value => hasTextContent(value) || '* Content is required',
                                    }}
                                    style={{border: '1px solid #d3d3d3'}}
                                    name="comment"
                                    key={`editor-${comment.id}`}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                    <AppButton isLoading={updateActivityLoading} onClick={handleSubmit(onSubmit)}>
                                        Save
                                    </AppButton>
                                    <AppButton
                                        color="secondary"
                                        variant="outlined"
                                        type="button"
                                        onClick={handleCommentEditClose}
                                    >
                                        Cancel
                                    </AppButton>
                                </Stack>
                            </Grid>
                        </Grid>
                    </form>
                ) : currentCommentStatus !== 'Updated' ? (
                    <Box> {parser(sanitizedHtml)}</Box>
                ) : null}
                {currentCommentStatus === 'Updated' &&
                    status.Updated &&
                    status.Updated.length > 0 &&
                    status.Updated.map((x, index) => (
                        <Box sx={{mt: 1}}>
                            <Typography variant="caption" color="textSecondary">
                                {`Previous Comment ${index + 1} : ${format(
                                    new Date(x.timestamp),
                                    'MM/dd/yyyy | hh:mm a',
                                )}`}
                            </Typography>
                            <Box sx={{ml: 1}}>{parser(x.value)}</Box>
                        </Box>
                    ))}
            </AppCard>

            <AppConfirmationDialog
                renderElement={(_open, handleOpen) => <></>}
                ref={commentConfirmationDialiogRef}
                icon={<Trash variant="Bold" />}
                onConfirmClick={(_open, _handleOpen, handleClose) => {
                    handleCommentDelete();
                    handleClose();
                }}
                title="Delete Comment"
                description="Are you sure you want to delete this comment?"
            />
        </Grid>
    );
};

export default BoardItemComment;
